import type { ReferenceGuide } from './types';
export const inventoryGuide: ReferenceGuide = {
  slug: 'inventory-management-systems',
  category: 'Inventory',
  accent: 'inventory',
  title: 'Inventory Management Systems',
  subtitle: 'How standalone inventory tools, ERP inventory modules, and a full WMS split the inventory lifecycle — and which one your complexity level actually calls for.',
  question: 'Do you actually need a WMS, or is ERP inventory enough?',
  description: 'A vendor-neutral guide to inventory systems: standalone tools like Cin7 and Fishbowl, ERP inventory in NetSuite and SAP, and warehouse management systems. What each tier owns and where each one stops.',
  readMinutes: 12,
  pdf: "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/hpwwowkqzxwbasxuhq2t8d/69c4a78d-9834-47a7-b2b9-f26dfaf63c78/inventory-management-systems-reference-packet.pdf",
  cta: {
    headline: 'See where your stock record and your floor disagree',
    button: 'Map your inventory flow'
  },
  sections: [{
    id: 'three-tiers',
    heading: 'The three tiers of inventory management',
    tocLabel: 'The three tiers',
    standfirst: 'Inventory is one of the most operationally critical assets a business owns. Managing it well requires the right system for your complexity level — and the three categories of software that handle inventory are not interchangeable.',
    blocks: [{
      kind: 'pillars',
      columns: 3,
      items: [{
        tag: 'INV',
        title: 'Inventory-only systems',
        layer: 'Track what you have and where it is',
        body: 'Cin7, Fishbowl, and inFlow are purpose-built to track stock levels, manage purchase orders, and give a real-time view of on-hand quantities. Fast to set up and affordable — but not designed for deep financial integration, demand planning, or complex warehouse operations.'
      }, {
        tag: 'ERP',
        title: 'ERP inventory',
        layer: 'Connect inventory to business operations',
        body: 'NetSuite, SAP, and Dynamics 365 make inventory the layer of a broader business operating system. Every stock movement posts to the general ledger automatically and feeds demand planning, procurement, and order management.',
        emphasis: 'accent'
      }, {
        tag: 'WMS',
        title: 'Warehouse management',
        layer: 'Orchestrate physical warehouse execution',
        body: 'Manhattan, Blue Yonder, and Extensiv go deeper than either. Where inventory systems track what you have and ERP tracks how much it cost, a WMS orchestrates how it physically moves — directing putaway, optimising pick paths, and guiding workers in real time.',
        emphasis: 'warn'
      }]
    }]
  }, {
    id: 'lifecycle',
    heading: 'The inventory lifecycle',
    tocLabel: 'The lifecycle',
    standfirst: 'Four stages, twelve steps, and a different system in the driver’s seat at each one.',
    blocks: [{
      kind: 'lifecycle',
      stages: [{
        label: 'Stage 1',
        title: 'Receipt & Receiving',
        steps: [{
          n: 1,
          title: 'PO created',
          body: 'Purchase order sent to supplier; expected receipt logged.'
        }, {
          n: 2,
          title: 'Goods arrive',
          body: 'Inbound shipment checked in; quantities and condition verified.'
        }, {
          n: 3,
          title: 'Inventory posted',
          body: 'Stock levels updated; cost basis recorded; items available.'
        }],
        note: '**All three system types handle receipt** — WMS adds directed putaway and dock scheduling.'
      }, {
        label: 'Stage 2',
        title: 'Storage & Replenishment',
        steps: [{
          n: 4,
          title: 'Putaway',
          body: 'Items stored; bin, shelf, or zone assigned.'
        }, {
          n: 5,
          title: 'Level monitoring',
          body: 'On-hand tracked; reorder points trigger replenishment.'
        }, {
          n: 6,
          title: 'Replenishment',
          body: 'Reorder via min/max rules, MRP, or manual review.'
        }],
        note: '**ERP handles demand-driven replenishment**; WMS handles physical slot replenishment.'
      }, {
        label: 'Stage 3',
        title: 'Picking & Fulfillment',
        steps: [{
          n: 7,
          title: 'Order received',
          body: 'Sales order creates fulfillment demand; pick list generated.'
        }, {
          n: 8,
          title: 'Pick & pack',
          body: 'Stock located, picked, and packed for shipment.'
        }, {
          n: 9,
          title: 'Inventory consumed',
          body: 'On-hand decremented; cost of goods recognised.'
        }],
        note: '**WMS optimises pick paths** and directs workers in real time — a gap for inventory-only systems.'
      }, {
        label: 'Stage 4',
        title: 'Visibility & Reconciliation',
        steps: [{
          n: 10,
          title: 'Cycle counts',
          body: 'Physical counts verify system accuracy; adjustments posted.'
        }, {
          n: 11,
          title: 'Shrink & adjustments',
          body: 'Damaged or lost inventory written off and reconciled.'
        }, {
          n: 12,
          title: 'Reporting',
          body: 'Valuation, turns, aging, and accuracy reports generated.'
        }],
        note: '**ERP integrates with financials**; WMS provides location-level accuracy; inventory systems offer basic reporting.',
        noteEmphasis: 'success'
      }]
    }]
  }, {
    id: 'inventory-only',
    heading: 'Inventory-only systems',
    tocLabel: 'Inventory-only tools',
    standfirst: 'What Cin7, Fishbowl, and similar tools do — and where they stop.',
    blocks: [{
      kind: 'compare',
      cards: [{
        tag: 'CIN7',
        title: 'Cin7 Core / Omni',
        body: 'Cloud-first platform strong in multichannel retail, wholesale, and e-commerce. Integrates with Shopify, Amazon, Xero, and QuickBooks.',
        bullets: ['Strong multichannel order management', '3PL and EDI connections', 'Light manufacturing / kitting support']
      }, {
        tag: 'FISH',
        title: 'Fishbowl Inventory',
        body: 'On-premise (and now cloud) inventory and light manufacturing tool with a strong install base among QuickBooks-connected small manufacturers.',
        bullets: ['Native QuickBooks sync (bidirectional)', 'Work orders and basic BOM support', 'Multi-location and bin tracking']
      }],
      columns: [],
      rows: []
    }, {
      kind: 'prose',
      paragraphs: ['These platforms handle the inventory fundamentals reliably: on-hand tracking across locations, purchase and sales order management, reorder points, barcode scanning, lot and serial number tracking, and integrations with popular accounting software. For businesses that need to know how much stock they have and when to reorder, they cover the core use case without ERP overhead.']
    }, {
      kind: 'callout',
      variant: 'warning',
      title: 'Where they hit their limits',
      body: 'Financial integration is shallow — they sync to accounting software but don’t own the general ledger, so valuation and COGS often need manual reconciliation. There is no demand planning: reorder points are static, set manually rather than calculated from demand signals, lead times, or seasonality. There is no warehouse execution — no directed putaway, no optimised pick routing, no real-time task assignment. And procurement, financials, sales, and manufacturing each live in separate tools, so keeping data consistent across those silos becomes its own full-time job as the business scales.'
    }]
  }, {
    id: 'erp-inventory',
    heading: 'Inventory in an ERP',
    tocLabel: 'ERP inventory',
    standfirst: 'In an ERP, inventory isn’t a standalone module — it’s the connective tissue between procurement, manufacturing, sales, and finance.',
    blocks: [{
      kind: 'prose',
      paragraphs: ['Every inventory movement posts to the general ledger automatically. Every stock level feeds demand planning. Every receipt updates landed cost and accounts payable. This integration is the fundamental difference between an ERP and an inventory-only system.']
    }, {
      kind: 'compare',
      columns: ['Capability', 'Inventory-only', 'ERP inventory'],
      rows: [['On-hand quantity tracking', 'Yes', 'Yes'], ['Multi-location / bin tracking', 'Basic', 'Advanced'], ['Automatic GL posting on inventory moves', 'No', 'Native'], ['Inventory valuation (FIFO, avg, standard)', 'Simplified', 'Full costing methods'], ['Landed cost tracking & allocation', 'Limited', 'Yes'], ['MRP / demand-driven replenishment', 'No', 'Yes'], ['Real-time available-to-promise (ATP)', 'Basic', 'Yes'], ['Manufacturing / BOM / work order integration', 'Light (Fishbowl only)', 'Yes'], ['Multi-subsidiary / intercompany transfers', 'No', 'Yes'], ['Directed warehouse execution', 'No', 'Basic (not WMS-depth)']]
    }, {
      kind: 'callout',
      variant: 'info',
      title: 'The key ERP insight',
      body: 'ERP inventory isn’t better at tracking stock — it’s better at connecting stock to everything else. When a movement posts to the GL, feeds an MRP run, updates ATP, and allocates cost automatically, the business runs with far fewer manual reconciliation steps than any standalone tool can provide.'
    }]
  }, {
    id: 'wms',
    heading: 'Warehouse management systems',
    tocLabel: 'WMS',
    standfirst: 'A WMS solves how do I move inventory efficiently and accurately — not just what do I have.',
    blocks: [{
      kind: 'prose',
      paragraphs: ['ERP tells you 500 units of SKU-1234 are available. A WMS tells the picker exactly which aisle, shelf, and bin to go to, in what sequence, and confirms via scan that they pulled the right item. Leading platforms include Manhattan Active WM, Blue Yonder, Körber, Extensiv, Logiwa, and NetSuite WMS.']
    }, {
      kind: 'pillars',
      columns: 3,
      items: [{
        tag: 'IN',
        title: 'Receiving & putaway',
        layer: 'Inbound',
        body: 'ASN matching and dock scheduling, directed putaway by zone, class, and velocity, license plate and pallet tracking, and cross-docking for high-velocity flow.'
      }, {
        tag: 'OUT',
        title: 'Picking & shipping',
        layer: 'Fulfillment',
        body: 'Wave, batch, zone, and cluster picking, optimised pick path routing, RF scanner / voice / pick-to-light, and pack verification with carrier labeling.'
      }, {
        tag: 'OPS',
        title: 'Inventory control',
        layer: 'Operations',
        body: 'Bin-level real-time accuracy, directed cycle counting, slot replenishment optimisation, and labor management with productivity tracking.'
      }]
    }, {
      kind: 'compare',
      columns: ['Capability', 'ERP inventory', 'WMS'],
      rows: [['Inventory quantity tracking', 'Warehouse-level', 'Bin / slot / license plate level'], ['Inventory valuation & GL integration', 'Native', 'Sends actuals back to ERP'], ['Directed putaway with slot rules', 'No', 'Yes'], ['Optimised pick path / wave management', 'No', 'Yes'], ['Real-time operator task direction (RF/voice)', 'No', 'Yes'], ['Dock scheduling & yard management', 'No', 'Yes'], ['MRP / demand-driven replenishment', 'Yes', 'No (receives from ERP)'], ['Financial reporting & P&L', 'Yes', 'No']]
    }, {
      kind: 'callout',
      variant: 'tip',
      title: 'WMS + ERP is the standard enterprise pattern',
      body: 'ERP manages the business layer — procurement, demand planning, financials, and order management. WMS manages the physical execution layer — every task, movement, and scan inside the warehouse. The WMS reports actuals back to ERP, keeping the financial and planning record clean.'
    }, {
      kind: 'callout',
      variant: 'flag',
      title: 'Right fit signal',
      body: 'You need a WMS when ERP inventory accuracy degrades at high volume, when pick errors and mis-ships are causing customer issues, when you have multiple warehouse zones, or when labor productivity is hard to measure. For companies with straightforward single-warehouse fulfillment, ERP inventory is usually sufficient.'
    }]
  }, {
    id: 'capability-matrix',
    heading: 'Capability ownership matrix',
    tocLabel: 'Capability matrix',
    standfirst: 'Which system owns which part of the inventory management lifecycle.',
    blocks: [{
      kind: 'matrix',
      columns: ['Inventory-only', 'ERP inventory', 'WMS'],
      legendNote: 'Cin7, Fishbowl · NetSuite, SAP · Manhattan, Extensiv',
      rows: [{
        capability: 'On-hand quantity tracking',
        values: [2, 2, 2]
      }, {
        capability: 'Multi-location / bin tracking',
        values: [1, 2, 2]
      }, {
        capability: 'Purchase order management',
        values: [2, 2, 0]
      }, {
        capability: 'Automatic GL & inventory costing',
        values: [0, 2, 1]
      }, {
        capability: 'Demand planning & MRP',
        values: [0, 2, 0]
      }, {
        capability: 'Available-to-promise / ATP',
        values: [1, 2, 0]
      }, {
        capability: 'Manufacturing / BOM integration',
        values: [1, 2, 0]
      }, {
        capability: 'Directed putaway & slotting',
        values: [0, 0, 2]
      }, {
        capability: 'Wave / batch / zone picking',
        values: [0, 0, 2]
      }, {
        capability: 'Real-time operator task direction',
        values: [0, 0, 2]
      }, {
        capability: 'Dock scheduling & yard management',
        values: [0, 0, 2]
      }, {
        capability: 'Labor productivity management',
        values: [0, 1, 2]
      }, {
        capability: 'Lot / serial / expiry traceability',
        values: [1, 2, 2]
      }, {
        capability: 'Cycle counting & inventory accuracy',
        values: [1, 1, 2]
      }, {
        capability: 'Multichannel / e-commerce integration',
        values: [2, 1, 1]
      }, {
        capability: 'Financial reporting & P&L',
        values: [0, 2, 0]
      }]
    }, {
      kind: 'callout',
      variant: 'tip',
      title: 'How to read this',
      body: 'No single system owns the full inventory lifecycle. Inventory-only tools cover the basics fast. ERP connects inventory to the business. WMS executes the physical warehouse. Most mature operations combine ERP + WMS; smaller companies choose based on their complexity level and growth trajectory.'
    }]
  }, {
    id: 'where-thruline-fits',
    heading: 'Where Thruline fits in',
    tocLabel: 'Where Thruline fits',
    blocks: [{
      kind: 'thrulineFit',
      intro: 'Thruline was built for the gap that shows up repeatedly in growing mid-market manufacturers: inventory data exists somewhere, but it isn’t connected to how product actually gets built and shipped. Depending on your current stack, Thruline fits in one of three ways.',
      scenarios: [{
        n: 1,
        condition: 'If you’re on spreadsheets, Cin7, Fishbowl, or another inventory-only system',
        headline: 'Thruline is your first real inventory system — or replaces the one you’ve outgrown',
        body: 'Thruline replaces rather than layers on top. It gives you real inventory tracking from day one, plus the full manufacturing execution layer — work orders, BOMs, digital work instructions, quality tracking, and as-built traceability — in one platform.',
        flow: [{
          label: 'Replaced by',
          title: 'Spreadsheets / Cin7 / Fishbowl',
          body: 'Inventory only, no manufacturing layer'
        }, {
          label: 'Thruline',
          title: 'Inventory + Manufacturing',
          body: 'Stock visibility and shop-floor execution in one platform',
          owned: true
        }],
        flowConnectors: ['next']
      }, {
        n: 2,
        condition: 'If you’re on an ERP like NetSuite with inventory process gaps',
        headline: 'Thruline is the execution layer that keeps ERP data clean',
        body: 'ERP data drifts when receipts are posted late, reorder points go stale, and teams work around the system. Thruline sits in front of the ERP inventory module as the execution layer — enforcing receiving and count procedures, surfacing tasks to the right people, and keeping records accurate through daily workflow rather than periodic cleanup.',
        features: [{
          title: 'Receiving workflows',
          body: 'Guided receipt procedures post to ERP accurately and on time — every time.'
        }, {
          title: 'Reorder management',
          body: 'Surfaces low-stock alerts to the right buyer before stockouts happen.'
        }, {
          title: 'Cycle count coordination',
          body: 'Directs count tasks and posts adjustments back to ERP automatically.'
        }, {
          title: 'Cross-team visibility',
          body: 'Shared view of inventory status without requiring ERP login complexity.'
        }]
      }, {
        n: 3,
        condition: 'If you need basic warehouse execution but aren’t running a full 3PL operation',
        headline: 'Thruline provides lightweight WMS functionality without a full implementation',
        body: 'Most mid-market manufacturers don’t need a full enterprise WMS. They need barcode scanning, bin tracking, and directed picks — without months of implementation. Thruline covers scan-based receiving, pick, and cycle count; bin and location tracking; directed tasks that tell operators exactly where to go; and scan-to-confirm that closes the loop back to the inventory record.',
        flow: [{
          label: 'ERP / Inventory',
          title: 'Stock record',
          body: 'Quantities, costs, orders'
        }, {
          label: 'Thruline',
          title: 'Scan, locate, direct',
          body: 'Barcode scanning, bin tracking, directed picks',
          owned: true
        }, {
          label: 'Warehouse',
          title: 'Operators & floor',
          body: 'Guided tasks, confirmed actions'
        }],
        flowConnectors: ['sync', 'next'],
        footnote: 'Right fit signal: if your team is receiving inventory on paper, picking from memory, or running cycle counts via spreadsheet — and you’re not ready for a full WMS implementation — this covers the gap without the six-month rollout.'
      }],
      solutions: [{
        label: 'Explore Inventory Management',
        to: '/solutions/inventory-management'
      }, {
        label: 'Explore ReceivePro',
        to: '/solutions/receivepro'
      }]
    }, {
      kind: 'callout',
      variant: 'bottomLine',
      title: 'The bottom line',
      body: 'Thruline meets you where you are. It replaces inventory-only tools with full manufacturing execution, layers onto ERP to enforce inventory accuracy, and provides the barcode-driven warehouse execution that growing manufacturers need before they’re ready for an enterprise WMS.'
    }]
  }]
};